import { createContext, useContext, useState } from "react";

const AdminLangCtx = createContext({ lang: "fr", setLang: () => {}, tr: (fr) => fr });

export const AdminLangProvider = ({ children }) => {
  const [lang, setLangState] = useState(localStorage.getItem("bt_admin_lang") === "en" ? "en" : "fr");
  const setLang = (l) => { localStorage.setItem("bt_admin_lang", l); setLangState(l); };
  const tr = (fr, en) => (lang === "en" ? (en ?? fr) : fr);
  return <AdminLangCtx.Provider value={{ lang, setLang, tr }}>{children}</AdminLangCtx.Provider>;
};

export const useAdminLang = () => useContext(AdminLangCtx);

export const STATUS_LABELS = {
  fr: { nouveau: "Nouveau", a_contacter: "À contacter", contacte: "Contacté", qualifie: "Qualifié", etude_technique: "Étude technique", devis_envoye: "Devis envoyé", gagne: "Gagné", perdu: "Perdu", archive: "Archivé" },
  en: { nouveau: "New", a_contacter: "To contact", contacte: "Contacted", qualifie: "Qualified", etude_technique: "Technical study", devis_envoye: "Quote sent", gagne: "Won", perdu: "Lost", archive: "Archived" },
};
export const SOURCE_LABELS = {
  fr: { coverage: "Couverture", quote: "Devis", contact: "Contact", application: "Candidature", newsletter: "Newsletter" },
  en: { coverage: "Coverage", quote: "Quote", contact: "Contact", application: "Application", newsletter: "Newsletter" },
};
export const ROLE_LABELS = {
  fr: { super_admin: "Super administrateur", editor: "Éditeur de contenu", sales: "Commercial", analyst: "Analyste (lecture seule)" },
  en: { super_admin: "Super administrator", editor: "Content editor", sales: "Sales", analyst: "Analyst (read-only)" },
};

// Central FR→EN dictionary for CMS field/section labels so editors are fully English.
export const FIELD_LABELS = {
  // Settings — section titles
  "Allégations techniques (modifiables / dépubliables)": "Technical claims (editable / unpublishable)",
  "Coordonnées de l'entreprise": "Company details",
  "Horaires d'ouverture": "Opening hours",
  "Réseaux sociaux (vide = non affiché)": "Social links (empty = hidden)",
  "Notifications internes": "Internal notifications",
  "Support réseau (affiché uniquement si activé et vérifié)": "Network support (shown only if enabled and verified)",
  "Conservation des données (mois)": "Data retention (months)",
  "SMTP — client e-mail interne (e-mails sortants)": "SMTP — internal email client (outgoing email)",
  // Settings — fields
  "Capacité IP Transit FR": "IP Transit capacity FR", "Capacité IP Transit EN": "IP Transit capacity EN",
  "Note capacité FR": "Capacity note FR", "Note capacité EN": "Capacity note EN",
  "Texte SLA FR": "SLA text FR", "Texte SLA EN": "SLA text EN",
  "Débit sans fil FR": "Fixed wireless throughput FR", "Débit sans fil EN": "Fixed wireless throughput EN",
  "Afficher le bouquet TV (hors navigation principale)": "Show TV bundle (outside main navigation)",
  "Libellé TV FR": "TV label FR", "Libellé TV EN": "TV label EN",
  "Nom (siège)": "Name (HQ)", "Adresse siège FR": "HQ address FR", "Adresse siège EN": "HQ address EN",
  "Téléphone RDC": "Phone DRC", "Téléphone Afrique du Sud": "Phone South Africa",
  "Site web": "Website", "ID. NAT.": "Nat. ID", "Horaires FR": "Hours FR", "Horaires EN": "Hours EN",
  "E-mail de notification des leads": "Lead notification email",
  "Activer l'option support": "Enable support option", "Numéro de support": "Support number", "URL du portail support": "Support portal URL",
  "Leads (mois)": "Leads (months)", "Messages (mois)": "Messages (months)",
  "Hôte SMTP": "SMTP host", "Utilisateur": "Username", "Adresse d'expédition": "From address", "SSL (port 465)": "SSL (port 465)",
  // Media slots
  "Logo du site (remplace le placeholder)": "Site logo (replaces the placeholder)",
  "Accueil — image héro": "Home — hero image", "Solutions — image héro": "Solutions — hero image",
  "Génie civil — visuel": "Civil works — visual", "Secteurs (détail) — image héro": "Sectors (detail) — hero image",
  "Accueil — mission / SD-WAN": "Home — mission / SD-WAN", "Équipe — visuel": "Team — visual",
  "Secteurs — image héro": "Sectors — hero image", "Visuel développeur": "Developer visual",
  "Solution satellite — visuel": "Satellite solution — visual", "Satellite — visuel terrain": "Satellite — field visual",
  "Connectivité rurale — visuel": "Rural connectivity — visual", "Communauté rurale — visuel": "Rural community — visual",
  "IP Transit / réseaux privés — visuel": "IP Transit / private networks — visual",
  "Internet sans fil — visuel": "Fixed wireless — visual", "Vidéosurveillance — visuel": "Video surveillance — visual",
  // Content fields
  "Slug (URL)": "Slug (URL)", "Catégorie": "Category",
  "Titre FR": "Title FR", "Titre EN": "Title EN", "Résumé FR": "Summary FR", "Résumé EN": "Summary EN",
  "Corps FR": "Body FR", "Corps EN": "Body EN", "Image": "Image",
  "Lieu": "Location", "Type de contrat": "Contract type", "Description FR": "Description FR", "Description EN": "Description EN",
  "Logo": "Logo", "Publié (accord confirmé)": "Published (agreement confirmed)",
  "Rôle FR": "Role FR", "Rôle EN": "Role EN", "Bio FR": "Bio FR", "Bio EN": "Bio EN", "Photo": "Photo", "Fondateur": "Founder",
  "Page (general, fibre-optique…)": "Page (general, fibre-optique…)",
  "Question FR": "Question FR", "Question EN": "Question EN", "Réponse FR": "Answer FR", "Réponse EN": "Answer EN",
  "Langue": "Language", "Fichier (PDF…)": "File (PDF…)",
  "Adresse FR": "Address FR", "Adresse EN": "Address EN", "Siège principal": "Primary HQ",
  // Catalogue fields
  "Résumé court": "Short summary", "Résultat pour le client": "Client outcome", "Aperçu technique": "Technical overview",
  "Capacités (une par ligne)": "Capabilities (one per line)", "Cas d'usage (un par ligne)": "Use cases (one per line)",
  "Segments (un par ligne)": "Segments (one per line)", "Avantages (un par ligne)": "Benefits (one per line)",
  "FAQ (Question :: Réponse, une par ligne)": "FAQ (Question :: Answer, one per line)",
  "Libellé du bouton (optionnel)": "Button label (optional)", "Points clés (un par ligne)": "Key points (one per line)",
  "Icône": "Icon",
  // Home editor
  "Héro": "Hero", "Bandeau crédibilité": "Credibility strip", "Mission": "Mission",
  "Section Solutions": "Solutions section", "Section Infrastructure": "Infrastructure section",
  "Section Secteurs": "Sectors section", "Section Connectivité rurale": "Rural connectivity section",
  "Pourquoi Bula": "Why Bula", "Écosystème": "Ecosystem", "CTA final": "Final CTA",
  "Sur-titre": "Eyebrow", "Titre — ligne 1": "Title — line 1", "Titre — ligne 2 (accent)": "Title — line 2 (accent)",
  "Sous-titre": "Subtitle", "Puces services (une par ligne)": "Service chips (one per line)",
  "Éléments": "Items", "Sous-texte": "Sub-text", "Encadré": "Highlight box", "Bouton": "Button",
  "Intro": "Intro", "Points (un par ligne)": "Points (one per line)", "Légende de la carte": "Map caption",
  "Phrase d'accent": "Accent phrase",
  // Shared field words
  "Titre": "Title", "Nom": "Name", "Ordre": "Order", "Publié": "Published", "Description": "Description",
  "Texte": "Text", "Libellé": "Label", "Type": "Type", "E-mail": "Email", "Téléphone": "Phone",
  "Meta title": "Meta title", "Meta description": "Meta description", "Port": "Port", "Mot de passe": "Password",
  "Retirer": "Remove", "+ Ajouter": "+ Add",
};

export const useLabel = () => {
  const { lang } = useAdminLang();
  return (fr) => (lang === "en" ? (FIELD_LABELS[fr] || fr) : fr);
};
